import { useState, useEffect } from "react";
import axios from "axios";
import { Package, FileText, CheckCircle, Clock, XCircle, RefreshCw, AlertCircle, Search, Filter } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function YarnRequirementAdminPage() {
  const [requirements, setRequirements] = useState([]);
  const [summary, setSummary] = useState({ total: 0, pending_for_po: 0, po_raised: 0, closed: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  
  // PO Raise Modal
  const [showPOModal, setShowPOModal] = useState(false);
  const [selectedReq, setSelectedReq] = useState(null);
  const [poNumber, setPONumber] = useState("");
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchRequirements();
    fetchSummary();
  }, []);

  const fetchRequirements = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/yarn-requirements`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequirements(response.data);
    } catch (err) {
      console.error("Failed to fetch yarn requirements:", err);
      setError(err.response?.data?.detail || "Failed to load yarn requirements");
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/yarn-requirements/stats/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSummary(response.data);
    } catch (err) {
      console.error("Failed to fetch summary:", err);
    }
  };

  const openPOModal = (req) => {
    setSelectedReq(req);
    setPONumber("");
    setShowPOModal(true);
  };

  const handleRaisePO = async () => {
    if (!selectedReq || !poNumber.trim()) return;
    
    setUpdating(true);
    setError("");
    
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/yarn-requirements/${selectedReq.id}/status`, {
        status: 'po_raised',
        po_number: poNumber
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess("PO raised successfully!");
      setShowPOModal(false);
      fetchRequirements();
      fetchSummary();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to raise PO");
    } finally {
      setUpdating(false);
    }
  };

  const handleCloseRequirement = async (reqId) => {
    if (!window.confirm("Are you sure you want to close this yarn requirement?")) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/yarn-requirements/${reqId}/status`, {
        status: 'closed'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess("Yarn requirement closed successfully!");
      fetchRequirements();
      fetchSummary();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to close requirement");
    }
  };

  const handleReopen = async (reqId) => {
    if (!window.confirm("Are you sure you want to reopen this yarn requirement?")) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/yarn-requirements/${reqId}/status`, {
        status: 'pending_for_po'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess("Yarn requirement reopened!");
      fetchRequirements();
      fetchSummary();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to reopen requirement");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending_for_po':
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300"><Clock className="w-3 h-3 mr-1" /> Pending for PO</Badge>;
      case 'po_raised':
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300"><FileText className="w-3 h-3 mr-1" /> PO Raised</Badge>;
      case 'closed':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300"><CheckCircle className="w-3 h-3 mr-1" /> Closed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const filteredRequirements = requirements.filter(req => {
    // Filter by status
    if (filter !== 'all' && req.status !== filter) return false;
    
    // Filter by search
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        req.wo_number?.toLowerCase().includes(search) ||
        req.customer_name?.toLowerCase().includes(search) ||
        req.yarn_type?.toLowerCase().includes(search) ||
        req.created_by?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center h-full">
        <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl text-primary">Yarn Requirements (Admin)</h1>
          <p className="text-muted-foreground">Manage yarn requirements from Weaving department</p>
        </div>
        <Button variant="outline" onClick={() => { fetchRequirements(); fetchSummary(); }}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {error && (
        <div className="bg-destructive/10 border border-destructive/30 text-destructive px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {error}
          <Button variant="ghost" size="sm" onClick={() => setError("")} className="ml-auto">✕</Button>
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-300 text-green-800 px-4 py-3 rounded-lg flex items-center gap-2">
          <CheckCircle className="w-5 h-5" />
          {success}
          <Button variant="ghost" size="sm" onClick={() => setSuccess("")} className="ml-auto">✕</Button>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="cursor-pointer hover:border-primary transition-colors" onClick={() => setFilter('all')}>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold">{summary.total}</div>
              <div className="text-sm text-muted-foreground">Total</div>
            </div>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer hover:border-yellow-500 transition-colors ${filter === 'pending_for_po' ? 'border-yellow-500' : ''}`} onClick={() => setFilter('pending_for_po')}>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-yellow-600">{summary.pending_for_po}</div>
              <div className="text-sm text-muted-foreground">Pending for PO</div>
            </div>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer hover:border-blue-500 transition-colors ${filter === 'po_raised' ? 'border-blue-500' : ''}`} onClick={() => setFilter('po_raised')}>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">{summary.po_raised}</div>
              <div className="text-sm text-muted-foreground">PO Raised</div>
            </div>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer hover:border-green-500 transition-colors ${filter === 'closed' ? 'border-green-500' : ''}`} onClick={() => setFilter('closed')}>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600">{summary.closed}</div>
              <div className="text-sm text-muted-foreground">Closed</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by WO, customer, yarn type, or submitted by..."
            className="pl-10"
          />
        </div>
        {filter !== 'all' && (
          <Button variant="outline" onClick={() => setFilter('all')}>
            <XCircle className="w-4 h-4 mr-2" />
            Clear Filter
          </Button>
        )}
      </div>

      {/* Requirements Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" />
            Yarn Requirements
            {filter !== 'all' && <Badge variant="outline">{filter.replace('_', ' ')}</Badge>}
          </CardTitle>
          <CardDescription>
            {filteredRequirements.length} requirement{filteredRequirements.length !== 1 ? 's' : ''} found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredRequirements.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No yarn requirements found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>WO Number</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Yarn Items</TableHead>
                    <TableHead>Required By</TableHead>
                    <TableHead>Submitted By</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead>PO #</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRequirements.map((req) => (
                    <TableRow key={req.id}>
                      <TableCell className="font-medium">{req.wo_number}</TableCell>
                      <TableCell>{req.customer_name}</TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {(req.yarn_items && req.yarn_items.length > 0 ? req.yarn_items : [{ yarn_type: req.yarn_type, color: req.color, required_qty: req.required_qty, unit: req.unit }]).map((item, idx) => (
                            <div key={idx} className="text-sm">
                              <span className="font-medium">{item.yarn_type}</span>
                              {item.color && <span className="text-muted-foreground"> ({item.color})</span>}
                              <span className="ml-2">{item.required_qty} {item.unit}</span>
                            </div>
                          ))}
                          {req.remarks && <div className="text-xs text-muted-foreground mt-1">Note: {req.remarks}</div>}
                        </div>
                      </TableCell>
                      <TableCell>{req.required_by_date}</TableCell>
                      <TableCell className="text-sm">{req.created_by}</TableCell>
                      <TableCell className="text-center">{getStatusBadge(req.status)}</TableCell>
                      <TableCell className="font-medium">{req.po_number || '-'}</TableCell>
                      <TableCell>
                        <div className="flex justify-center gap-2 flex-wrap">
                          {req.status === 'pending_for_po' && (
                            <Button 
                              variant="default" 
                              size="sm" 
                              className="min-h-[44px] bg-blue-600 hover:bg-blue-700"
                              onClick={() => openPOModal(req)}
                            >
                              <FileText className="w-4 h-4 mr-1" />
                              Raise PO
                            </Button>
                          )}
                          {req.status === 'po_raised' && (
                            <Button 
                              variant="default" 
                              size="sm" 
                              className="min-h-[44px] bg-green-600 hover:bg-green-700"
                              onClick={() => handleCloseRequirement(req.id)}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Close
                            </Button>
                          )}
                          {req.status === 'closed' && (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="min-h-[44px]"
                              onClick={() => handleReopen(req.id)}
                            >
                              <RefreshCw className="w-4 h-4 mr-1" />
                              Reopen
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}}
        </CardContent>
      </Card>

      {/* Raise PO Modal */}
      <Dialog open={showPOModal} onOpenChange={setShowPOModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Raise Yarn Purchase Order</DialogTitle>
            <DialogDescription>
              Enter the PO number for yarn requirement
            </DialogDescription>
          </DialogHeader>
          
          {selectedReq && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Work Order:</span>
                  <span className="font-semibold">{selectedReq.wo_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Customer:</span>
                  <span className="font-semibold">{selectedReq.customer_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Required By:</span>
                  <span className="font-semibold">{selectedReq.required_by_date}</span>
                </div>
              </div>
              
              {/* Yarn Items */}
              <div className="space-y-2">
                <Label className="font-semibold">Yarn Items</Label>
                <div className="bg-slate-50 rounded-lg p-3 max-h-[200px] overflow-y-auto">
                  {(selectedReq.yarn_items && selectedReq.yarn_items.length > 0 
                    ? selectedReq.yarn_items 
                    : [{ yarn_type: selectedReq.yarn_type, color: selectedReq.color, required_qty: selectedReq.required_qty, unit: selectedReq.unit }]
                  ).map((item, idx) => (
                    <div key={idx} className="flex justify-between py-1 border-b border-slate-200 last:border-0">
                      <span className="font-medium">{item.yarn_type} {item.color && `(${item.color})`}</span>
                      <span className="font-bold">{item.required_qty} {item.unit}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>PO Number *</Label>
                <Input
                  value={poNumber}
                  onChange={(e) => setPONumber(e.target.value)}
                  placeholder="Enter Yarn PO Number"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPOModal(false)} className="min-h-[44px]">Cancel</Button>
            <Button onClick={handleRaisePO} disabled={updating || !poNumber.trim()} className="min-h-[44px]">
              {updating ? 'Saving...' : 'Raise PO'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
